import type { VgAnswerValue, VgField } from '../../../types/vorstellungsgespraech';

interface VgFieldInputProps {
  field: VgField;
  value: VgAnswerValue | undefined;
  onChange: (value: VgAnswerValue) => void;
  disabled?: boolean;
}

const choiceClasses = (disabled: boolean) =>
  `flex items-center gap-2 py-1.5 text-sm text-gray-700 ${
    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
  }`;

export function VgFieldInput({ field, value, onChange, disabled = false }: VgFieldInputProps) {
  const { key, type, label, placeholder, options = [], suffix, min, max, rows = 3, body } = field;

  const numberValue = typeof value === 'number' || typeof value === 'string' ? String(value) : '';

  switch (type) {
    case 'freitext':
      return (
        <textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
        />
      );

    case 'text':
      return (
        <div className="flex items-center gap-2">
          {label && <span className="text-sm text-gray-600 w-40 flex-shrink-0">{label}</span>}
          <input
            type="text"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
      );

    case 'info':
      return (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          {label && <p className="text-xs font-semibold text-gray-700">{label}</p>}
          {body && (
            <div className={`space-y-1 text-sm text-gray-600 ${label ? 'mt-1' : ''}`}>
              {body.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          )}
        </div>
      );

    case 'single':
      return (
        <div>
          {options.map((option) => (
            <label key={option.value} className={choiceClasses(disabled)}>
              <input
                type="radio"
                name={key}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                disabled={disabled}
                className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {option.label}
            </label>
          ))}
        </div>
      );

    case 'multi': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div>
          {options.map((option) => (
            <label key={option.value} className={choiceClasses(disabled)}>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, option.value]
                      : selected.filter((v) => v !== option.value),
                  )
                }
                disabled={disabled}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {option.label}
            </label>
          ))}
        </div>
      );
    }

    case 'boolean':
      return (
        <div className="flex items-center gap-2">
          {label && <span className="text-sm text-gray-600 w-40 flex-shrink-0">{label}</span>}
          <div className="flex items-center gap-6">
            {[
              { value: true, label: 'Ja' },
              { value: false, label: 'Nein' },
            ].map((option) => (
              <label key={option.label} className={choiceClasses(disabled)}>
                <input
                  type="radio"
                  name={key}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                  disabled={disabled}
                  className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      );

    case 'date':
      return (
        <div className="flex items-center gap-2">
          {label && <span className="text-sm text-gray-600 w-40 flex-shrink-0">{label}</span>}
          <input
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={disabled}
            className="rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
      );

    case 'time':
      return (
        <div className="flex items-center gap-2">
          {label && <span className="text-sm text-gray-600 w-40 flex-shrink-0">{label}</span>}
          <input
            type="time"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={disabled}
            className="rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
      );

    case 'number':
      return (
        <div className="flex items-center gap-2">
          {label && <span className="text-sm text-gray-600 w-40 flex-shrink-0">{label}</span>}
          <input
            type="number"
            value={numberValue}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            disabled={disabled}
            min={min}
            max={max}
            step="any"
            className="w-28 rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
          {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
        </div>
      );

    default:
      return null;
  }
}
